<script setup>
import { computed, ref, watch } from 'vue'
import SelectableListItem from './components/SelectableListItem.vue'
import { isEqual } from 'lodash-es'
import { getLeafIds } from '@/utils/getLeafIds'
import { defaultVTooltipProps } from '@/utils/defaultProps'

const LOCAL_STORAGE_KEY_PREFIX = 'nested-category-select:expanded:'

const props = defineProps({
  activatorLabel: {
    type: String,
    default: 'Select',
  },

  // plain array of selected ids (primitives)
  value: {
    type: Array,
    default: () => [],
  },

  // nested tree items with runtime validation
  /**
   * nested array of nodes. Each node is an object:
   *   {
   *     id: string|number,            // required leaf id or internal node id
   *     name?: string,               // label shown in UI
   *     description?: string,        // optional tooltip/description
   *     loading?: boolean,           // optional loading state to show skeleton and disable interaction
   *     hideInSelect?: boolean,      // optional flag to hide node in select
   *     children?: Array<node>       // optional child nodes with same shape
   *   }
   *
   */
  treeItems: {
    type: Array,
    required: true,
  },

  loading: {
    type: Boolean,
    default: false,
  },

  isFilterEnabled: {
    type: Boolean,
    default: true,
  },

  selectionCountMode: {
    type: String,
    default: 'ids',
    validator: (value) => ['ids', 'branches'].includes(value),
  },

  allowInitialEmptySelection: {
    type: Boolean,
    default: false,
  },

  groupTitleClass: {
    type: String,
    default: 'font-weight-bold text-uppercase',
  },

  tooltipListItemSide: { type: String, default: 'right' },

  vBtnProps: {
    type: Object,
    default: () => ({}),
  },

  vMenuProps: {
    type: Object,
    default: () => ({}),
  },

  menuWidth: {
    type: [String, Number],
    default: '30rem',
  },

  expandedStateStorageKey: {
    type: String,
    default: '',
  },

  maxListHeight: {
    type: [String, Number],
    default: '20rem',
  },
})

const emit = defineEmits(['input'])

const selectedIds = ref(props.value)
const searchQuery = ref('')
const expandedNodeIds = ref([])
const hasAutoExpandedInSearchSession = ref(false)

const getNodeLabel = (node) => node?.name ?? node?.id
const normalizeNodeId = (id) => String(id)
const isDescriptionHelpful = (node) => !!node?.description && getNodeLabel(node) !== node.description
const getTooltipActivatorId = (node) =>
  `nested-category-select-tooltip-${normalizeNodeId(node?.id)
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9_-]/g, '-')}`

const sanitizeStorageKeyPart = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9:_-]/g, '-')
    .toLowerCase()

const getExpandableNodeIds = (nodes = []) =>
  nodes.flatMap((node) => {
    const children = Array.isArray(node?.children) ? node.children : []
    const nestedIds = getExpandableNodeIds(children)

    if (!children.length || node?.hideInSelect) return nestedIds

    return [normalizeNodeId(node.id), ...nestedIds]
  })

const expandedStateStorageKey = computed(() => {
  if (props.expandedStateStorageKey) {
    return `${LOCAL_STORAGE_KEY_PREFIX}${sanitizeStorageKeyPart(props.expandedStateStorageKey)}`
  }

  const activatorPart = sanitizeStorageKeyPart(props.activatorLabel || 'default')
  const topLevelIdsPart = props.treeItems
    .map((node) => sanitizeStorageKeyPart(node?.id))
    .filter(Boolean)
    .join('|')

  return `${LOCAL_STORAGE_KEY_PREFIX}${activatorPart}:${topLevelIdsPart || 'tree'}`
})

const loadExpandedNodeIds = () => {
  if (typeof window === 'undefined') return []

  try {
    const rawValue = window.localStorage.getItem(expandedStateStorageKey.value)
    const parsedValue = rawValue ? JSON.parse(rawValue) : []
    return Array.isArray(parsedValue) ? parsedValue.map(normalizeNodeId) : []
  } catch (error) {
    return []
  }
}

const persistExpandedNodeIds = (nodeIds) => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(expandedStateStorageKey.value, JSON.stringify(nodeIds))
  } catch (error) {
    // Ignore storage write failures.
  }
}

const areChildrenVisible = (children) => children.some((child) => !child?.hideInSelect)

const isExpandableNode = (node) =>
  !node?.hideInSelect && Array.isArray(node?.children) && node.children.length > 0 && areChildrenVisible(node.children)
// const isBrunch = (node) => node?.depth === 0 && node?.isExpandable
const isBrunch = (node) => node?.isExpandable
const getNodeRowPaddingClass = (node) => (node?.depth ? `pl-${node.depth * 8}` : '')
const isNodeRowDisabled = (node) => node?.loading || (node?.isExpandable && isSearchMode.value)

const onNodeRowClick = (node) => {
  if (node?.loading) return

  if (node?.isExpandable) {
    toggleNodeExpanded(node?.id)
    return
  }

  updateSelectionByNode({ checked: node?.whatAreChecked !== 'all', value: node?.id })
}

const isNodeExpanded = (nodeId) => expandedNodeIds.value.includes(normalizeNodeId(nodeId))

const toggleNodeExpanded = (nodeId) => {
  const normalizedNodeId = normalizeNodeId(nodeId)

  expandedNodeIds.value = isNodeExpanded(normalizedNodeId)
    ? expandedNodeIds.value.filter((id) => id !== normalizedNodeId)
    : [...expandedNodeIds.value, normalizedNodeId]
}

const findNodeById = (nodes, id) => {
  const targetId = String(id)

  for (const node of nodes || []) {
    if (String(node?.id) === targetId) return node

    const children = Array.isArray(node?.children) ? node.children : []
    if (children.length) {
      const found = findNodeById(children, targetId)
      if (found) return found
    }
  }
  return undefined
}

const getLeafIdsByNodeId = (nodes, nodeId) => {
  const targetNode = findNodeById(nodes, nodeId)
  return targetNode ? getLeafIds(targetNode) : []
}

const mapNodesWithCheckedStatus = (nodes) =>
  nodes.map((node) => {
    const leafIds = getLeafIds(node)
    const selectedCount = leafIds.filter((id) => selectedIds.value?.includes(id)).length

    const whatAreChecked = leafIds.length
      ? selectedCount === leafIds.length
        ? 'all'
        : selectedCount > 0
          ? 'some'
          : 'none'
      : 'none'

    return {
      ...node,
      whatAreChecked,
      children: node.children ? mapNodesWithCheckedStatus(node.children) : [],
    }
  })

const normalizeText = (value) =>
  String(value ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/\./g, '')
    .toLowerCase()
    .trim()

const filteredTreeItems = computed(() => {
  if (!searchQuery.value) return props.treeItems

  const filterNodes = (nodes) =>
    nodes.reduce((acc, node) => {
      const label = getNodeLabel(node)

      if (normalizeText(label).includes(searchQuery.value.toLowerCase())) {
        acc.push(node)
        return acc
      }

      if (!node?.children) return acc

      const filteredChildren = filterNodes(node.children)

      if (filteredChildren.length) {
        acc.push({
          ...node,
          children: filteredChildren,
        })
      }

      return acc
    }, [])

  return filterNodes(props.treeItems)
})

const isSearchMode = computed(() => !!searchQuery.value)

const effectiveExpandedNodeIds = computed(() => expandedNodeIds.value)

const flattenVisibleNodes = (nodes = [], depth = 0) =>
  nodes.flatMap((node) => {
    const children = Array.isArray(node?.children) ? node.children : []
    const isExpandable = isExpandableNode(node)
    const normalizedNode = {
      ...node,
      depth,
      isExpandable,
      isExpanded: isExpandable ? effectiveExpandedNodeIds.value.includes(normalizeNodeId(node.id)) : false,
    }

    const visibleChildren =
      (node?.hideInSelect || normalizedNode.isExpanded) && children.length
        ? flattenVisibleNodes(children, depth + 1)
        : []

    if (node?.hideInSelect) return visibleChildren

    return [normalizedNode, ...visibleChildren]
  })

const visibleFlatNodes = computed(() => {
  return flattenVisibleNodes(mapNodesWithCheckedStatus(filteredTreeItems.value))
})

const visibleExpandableNodeIds = computed(() => getExpandableNodeIds(filteredTreeItems.value))

const areAllVisibleBranchesExpanded = computed(() => {
  if (!visibleExpandableNodeIds.value.length) return false

  return visibleExpandableNodeIds.value.every((nodeId) => expandedNodeIds.value.includes(nodeId))
})

const allFilteredLeafIds = computed(() => getLeafIds(filteredTreeItems.value))

const filteredSelectionStatus = computed(() => {
  if (!allFilteredLeafIds.value?.length) return 'none'
  if (allFilteredLeafIds.value.every((id) => selectedIds.value.includes(id))) return 'all'
  if (allFilteredLeafIds.value.some((id) => selectedIds.value.includes(id))) return 'some'
  return 'none'
})

const applySelectionByLeafIdsAndEmit = ({ checked, leafIds }) => {
  if (checked) {
    selectedIds.value = [...new Set([...selectedIds.value, ...leafIds])]
  } else {
    selectedIds.value = selectedIds.value.filter((id) => !leafIds.includes(id))
  }
  // emit('input', [...partitionedValueIds.value.excludedFromTree, ...selectedIds.value])
}

const toggleAllFiltered = (checked) => {
  applySelectionByLeafIdsAndEmit({
    checked,
    leafIds: allFilteredLeafIds.value,
  })
}

const updateSelectionByNode = ({ checked, value: nodeId }) => {
  applySelectionByLeafIdsAndEmit({
    checked,
    leafIds: getLeafIdsByNodeId(filteredTreeItems.value, nodeId),
  })
}

const getLeafIdsByTopNodesWithSelectionPriority = (newTreeItems) => {
  const leafArrays = newTreeItems.map((id) => {
    return getLeafIdsByNodeId(props.treeItems, id)
  })

  return leafArrays.flatMap((leafIds) => {
    const leafIdsWithSelected = leafIds.filter((leafId) => selectedIds.value.includes(leafId))
    return leafIdsWithSelected.length > 0 ? leafIdsWithSelected : leafIds
  })
}

const allTreeLeafIds = computed(() => (props?.treeItems ? getLeafIds(props?.treeItems) : []))

const selectionIdsCountLabel = computed(() => {
  return `${selectedIds.value.length}/${allTreeLeafIds.value.length}`
})

const pruneTreeToSelectedBranches = (nodeIds, selectedIds) => {
  if (!nodeIds?.length) return []

  const pruneNodeBySelection = (node) => {
    if (selectedIds.includes(node?.id)) return node

    const children = Array.isArray(node?.children) ? node.children : []
    const prunedChildren = children.map(pruneNodeBySelection).filter(Boolean)

    if (!prunedChildren.length) return null

    return {
      ...node,
      children: prunedChildren,
    }
  }

  return nodeIds?.map(pruneNodeBySelection).filter(Boolean)
}

const selectedBranches = computed(() => pruneTreeToSelectedBranches(props.treeItems, selectedIds.value))

const totalBranchCount = computed(() => props.treeItems?.length ?? 0)
const selectedBranchCount = computed(() => selectedBranches.value?.length ?? 0)
const selectionBranchCountLabel = computed(() => `${selectedBranchCount.value}/${totalBranchCount.value}`)

const selectionCountLabel = computed(() =>
  props.selectionCountMode === 'branches' ? selectionBranchCountLabel.value : selectionIdsCountLabel.value,
)

watch(
  () => props.treeItems.map((node) => node?.id),
  (newIds) => {
    if (props.allowInitialEmptySelection) return

    selectedIds.value = getLeafIdsByTopNodesWithSelectionPriority(newIds)
  },
  {
    immediate: true,
  },
)

watch(
  selectedIds,
  (newSelectedIds, oldSelectedIds) => {
    if (isEqual(newSelectedIds, oldSelectedIds)) return

    // console.log('Selected ids changed:', newSelectedIds)
    emit('input', newSelectedIds)
    emit('update:value', newSelectedIds)

    // const selectedNodes = newSelectedIds.map((id) => findNodeById(props.treeItems, id)).filter(Boolean)
    // emit('update:selectedNodes', selectedNodes)
    emit('update:selectedBranches', selectedBranches.value)
  },
  { immediate: true },
)

watch(
  () => props.loading,
  (newValue, oldValue) => {
    // if (isEmpty(newValue)) return
    if (oldValue === true && newValue === false) selectedIds.value = props.value
  },
  { immediate: true },
)

const bulkSelectionLabel = computed(() => {
  if (!searchQuery.value) return 'ALL'
  return filteredSelectionStatus.value === 'all' ? 'Subtract All Filtered' : 'Add All Filtered'
})

const branchToggleLabel = computed(() => (areAllVisibleBranchesExpanded.value ? 'Collapse All' : 'Expand All'))

const menu = ref(null)

const resetSearchQuery = (val) => {
  if (!val) searchQuery.value = ''
}

const toggleVisibleBranches = () => {
  if (!visibleExpandableNodeIds.value.length) return

  if (areAllVisibleBranchesExpanded.value) {
    expandedNodeIds.value = expandedNodeIds.value.filter((id) => !visibleExpandableNodeIds.value.includes(id))
    return
  }

  expandedNodeIds.value = [...new Set([...expandedNodeIds.value, ...visibleExpandableNodeIds.value])]
}

watch(
  isSearchMode,
  (isFiltering) => {
    if (!isFiltering) {
      hasAutoExpandedInSearchSession.value = false
      return
    }

    if (hasAutoExpandedInSearchSession.value) return

    expandedNodeIds.value = [...new Set([...expandedNodeIds.value, ...getExpandableNodeIds(filteredTreeItems.value)])]
    hasAutoExpandedInSearchSession.value = true
  },
  { immediate: true },
)

watch(
  expandedStateStorageKey,
  () => {
    expandedNodeIds.value = loadExpandedNodeIds()
  },
  { immediate: true },
)

watch(expandedNodeIds, (newExpandedNodeIds) => {
  persistExpandedNodeIds(newExpandedNodeIds)
})

watch(
  () => getExpandableNodeIds(props.treeItems),
  (expandableNodeIds) => {
    if (props.loading && !expandableNodeIds.length) return

    const normalizedExpandableIds = expandableNodeIds.map(normalizeNodeId)
    expandedNodeIds.value = expandedNodeIds.value.filter((id) => normalizedExpandableIds.includes(id))
  },
  { immediate: true },
)
</script>

<template>
  <v-menu
    v-model="menu"
    offset-y
    v-bind="{
      // maxHeight: '25rem',
      ...vMenuProps,
    }"
    :close-on-content-click="false"
    @input="resetSearchQuery"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        v-bind="{ ...attrs, ...vBtnProps }"
        v-on="on"
        :loading="loading"
        aria-label="Show menu button"
      >
        {{ activatorLabel }} ({{ selectionCountLabel }})
        <v-icon
          dense
          right
        >
          mdi-menu-down
        </v-icon>
      </v-btn>
    </template>

    <v-card
      class="p-1"
      :style="menuWidth ? { minWidth: typeof menuWidth === 'number' ? `${menuWidth}px` : menuWidth } : {}"
    >
      <div class="d-flex flex-column">
        <b-form-input
          v-if="isFilterEnabled"
          v-model="searchQuery"
          autocomplete="off"
          placeholder="Filter"
        />

        <div class="d-flex align-center pr-2 nested-select-controls">
          <SelectableListItem
            :title="bulkSelectionLabel"
            :check-box-value="filteredSelectionStatus"
            :indeterminate-value="filteredSelectionStatus === 'some'"
            :disabled="!visibleFlatNodes.length"
            list-item-class="py-0"
            title-class="font-weight-bold text-uppercase"
            class="flex-grow-1"
            @input="({ checked }) => toggleAllFiltered(checked)"
          />

          <v-tooltip
            right
            v-bind="defaultVTooltipProps"
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="ml-2 nested-select-toggle-all"
                :disabled="!visibleExpandableNodeIds.length"
                v-bind="attrs"
                v-on="on"
                @click="toggleVisibleBranches"
                aria-label="Toggle branches button"
              >
                <v-icon small>
                  {{ areAllVisibleBranchesExpanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}
                </v-icon>
              </v-btn>
            </template>

            <span>{{ branchToggleLabel }}</span>
          </v-tooltip>
        </div>
      </div>

      <v-divider
        style="border-color: #e9ecef"
        class="my-1"
      />

      <v-list
        flat
        dense
        :max-height="maxListHeight"
        class="overflow-auto"
      >
        <div
          v-for="node in visibleFlatNodes"
          :key="node?.id"
        >
          <v-list-item
            dense
            class="hover-bg-primary nested-group-item"
            :class="getNodeRowPaddingClass(node)"
            @click="onNodeRowClick(node)"
            :style="isBrunch(node) ? {} : { cursor: 'default' }"
            :ripple="isBrunch(node)"
          >
            <v-list-item-action class="mr-2 my-1 nested-group-action">
              <button
                v-if="isBrunch(node)"
                type="button"
                class="nested-group-toggle"
                :disabled="node?.loading"
                @click.stop="toggleNodeExpanded(node?.id)"
              >
                <v-icon>
                  {{ node?.isExpanded ? 'mdi-menu-down' : 'mdi-menu-right' }}
                </v-icon>
              </button>

              <span
                v-else
                class="nested-group-toggle-spacer"
              />

              <v-simple-checkbox
                :value="node?.whatAreChecked === 'all'"
                :indeterminate="node?.whatAreChecked === 'some'"
                color="primary"
                :ripple="false"
                @click.stop="updateSelectionByNode({ checked: node?.whatAreChecked !== 'all', value: node?.id })"
              />
            </v-list-item-action>

            <v-list-item-content>
              <v-list-item-title :class="node.depth === 0 ? groupTitleClass : ''">
                <span v-html="getNodeLabel(node)" />
              </v-list-item-title>
            </v-list-item-content>

            <v-list-item-icon>
              <v-progress-circular
                v-if="node?.loading"
                indeterminate
                :size="15"
                :width="2"
                color="primary"
                class="mx-auto my-auto"
              />

              <v-icon
                v-else-if="isDescriptionHelpful(node)"
                :id="getTooltipActivatorId(node)"
                small
                style="cursor: help"
              >
                mdi-information-outline
              </v-icon>
            </v-list-item-icon>
          </v-list-item>

          <v-menu
            v-if="isDescriptionHelpful(node)"
            :activator="`#${getTooltipActivatorId(node)}`"
            :close-on-content-click="false"
            max-height="20rem"
            open-on-hover
            v-bind="defaultVTooltipProps"
            offset-x
            nudge-top="20"
            :right="tooltipListItemSide === 'right'"
            :left="tooltipListItemSide === 'left'"
          >
            <v-card>
              <v-card-text>
                <span v-html="node?.description" />
              </v-card-text>
            </v-card>
          </v-menu>
        </div>
      </v-list>
    </v-card>
  </v-menu>
</template>

<style scoped>
.nested-select-controls {
  min-height: 3rem;
}

.nested-select-toggle-all {
  width: 2rem;
  height: 2rem;
}

.nested-group-item {
  min-height: 2.5rem;
}

.nested-group-action {
  min-width: 3rem;
  flex-direction: row !important;
  align-items: center;
}

.nested-group-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 0.1rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.nested-group-toggle:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.04);
}

.nested-group-toggle:disabled {
  cursor: default;
}

.nested-group-toggle-spacer {
  display: inline-block;
  width: 24px;
  height: 24px;
  margin-right: 0.1rem;
  flex: 0 0 24px;
}

.nested-group-action :deep(.v-input--selection-controls) {
  margin-top: 0;
  padding-top: 0;
}

.nested-group-action :deep(.v-input__slot) {
  margin-bottom: 0;
}

.hover-bg-primary {
  transition: background-color 0.15s ease-in-out;
}

.hover-bg-primary:hover {
  background-color: rgba(var(--v-primary-base-rgb, 25, 118, 210), 0.08);
}
</style>
